import { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { Keypair, PublicKey } from '@solana/web3.js'
import { remember, restore, forget } from '../utils/unlockCache'
import { getPrfSecret, enrollAndGetSecret, PRF_SALT_SOLANA } from '../utils/webauthnPrf'
import { isPaperActive, isPaperAdded, setPaperActive, PAPER_EVENT } from '../utils/paperTrade'   // paperwallet-20261027

const WalletContext = createContext(null)

// ─── AES-GCM encryption helpers (Web Crypto API — no dependencies) ───

async function deriveKey(password, salt) {
  const enc = new TextEncoder()
  const keyMaterial = await crypto.subtle.importKey(
    'raw', enc.encode(password), 'PBKDF2', false, ['deriveKey']
  )
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: 600000, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  )
}

async function encryptData(plaintext, password) {
  const enc = new TextEncoder()
  const salt = crypto.getRandomValues(new Uint8Array(16))
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const key = await deriveKey(password, salt)
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    enc.encode(plaintext)
  )
  // Pack: salt(16) + iv(12) + ciphertext
  const packed = new Uint8Array(salt.length + iv.length + ciphertext.byteLength)
  packed.set(salt, 0)
  packed.set(iv, 16)
  packed.set(new Uint8Array(ciphertext), 28)
  return btoa(String.fromCharCode(...packed))
}

async function decryptData(packed64, password) {
  const packed = Uint8Array.from(atob(packed64), c => c.charCodeAt(0))
  const salt = packed.slice(0, 16)
  const iv = packed.slice(16, 28)
  const ciphertext = packed.slice(28)
  const key = await deriveKey(password, salt)
  const dec = new TextDecoder()
  const plainBuf = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext
  )
  return dec.decode(plainBuf)
}

// ─── Base58 encode/decode (no dependency) ───

const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz'

function bs58decode(str) {
  const bytes = []
  for (const c of str) {
    const carry = BASE58_ALPHABET.indexOf(c)
    if (carry < 0) throw new Error('Invalid base58 character')
    for (let j = 0; j < bytes.length; j++) {
      const x = bytes[j] * 58 + carry
      bytes[j] = x & 0xff
      // carry forward handled below
    }
    // Re-process carries
    let carry2 = 0
    for (let j = 0; j < bytes.length; j++) {
      const x = bytes[j] * 58 + carry2
      // Oops — simpler approach: rebuild
    }
  }
  // Use a BigInt approach for correctness
  let num = 0n
  for (const c of str) {
    num = num * 58n + BigInt(BASE58_ALPHABET.indexOf(c))
  }
  const hex = num.toString(16).padStart(2, '0')
  const rawBytes = hex.match(/.{1,2}/g).map(b => parseInt(b, 16))
  // Count leading '1's → leading zero bytes
  let leadingZeros = 0
  for (const c of str) { if (c === '1') leadingZeros++; else break }
  return new Uint8Array([...new Array(leadingZeros).fill(0), ...rawBytes])
}

function bs58encode(bytes) {
  let num = 0n
  for (const b of bytes) num = num * 256n + BigInt(b)
  let str = ''
  while (num > 0n) {
    str = BASE58_ALPHABET[Number(num % 58n)] + str
    num = num / 58n
  }
  for (const b of bytes) { if (b === 0) str = '1' + str; else break }
  return str
}

// ─── Provider ───

const STORAGE_KEY = 'solcheddar_encrypted_wallet'
// Biometric envelope. SEPARATE key, never a replacement: the password blob
// above stays untouched, so a lost/changed passkey costs convenience, never
// the wallet. Same ciphertext format — only the passphrase differs (a PRF
// secret instead of a typed one).
const BIO_KEY = STORAGE_KEY + '_bio'
// (addwallet-20261027) More wallets. The ACTIVE wallet stays in STORAGE_KEY /
// BIO_KEY exactly as before, so nothing downstream changes. The others sit in
// SLOTS_KEY as [{ pub, enc, bio }] — the same encrypted blobs, just parked.
// Switching swaps the chosen blob into STORAGE_KEY and parks the current one.
const SLOTS_KEY = 'solcheddar_wallet_slots'
const ACTIVE_PUB_KEY = 'solcheddar_wallet_active_pub'
function readSlots() {
  try {
    const a = JSON.parse(localStorage.getItem(SLOTS_KEY) || '[]')
    return Array.isArray(a) ? a.filter(x => x && x.pub && x.enc) : []
  } catch { return [] }
}
function writeSlots(a) {
  if (a.length) localStorage.setItem(SLOTS_KEY, JSON.stringify(a))
  else localStorage.removeItem(SLOTS_KEY)
}
function parseSecret(privateKeyInput) {
  let secretKey
  try {
    const parsed = JSON.parse(privateKeyInput)
    if (Array.isArray(parsed)) secretKey = new Uint8Array(parsed)
  } catch {}
  if (!secretKey) {
    try { secretKey = bs58decode(privateKeyInput.trim()) }
    catch { throw new Error('Invalid private key format. Use base58 or JSON byte array.') }
  }
  return Keypair.fromSecretKey(secretKey)
}

export function LocalWalletProvider({ children }) {
  const [publicKey, setPublicKey] = useState(null)
  const [isLocked, setIsLocked] = useState(true)
  const [hasStoredWallet, setHasStoredWallet] = useState(false)
  const [hasBiometric, setHasBiometric] = useState(false)
  const keypairRef = useRef(null)
  const [otherWallets, setOtherWallets] = useState([])   // pubs of parked wallets
  const [activePub, setActivePub] = useState(null)
  const markActive = (pub) => { localStorage.setItem(ACTIVE_PUB_KEY, pub); setActivePub(pub) }
  // (paperwallet-20261027) Paper wallet: no key. While active the real key is
  // dropped from memory (locked) and signTransaction refuses.
  const [paperActive, setPaperActiveState] = useState(isPaperActive)
  const [paperAdded, setPaperAdded] = useState(isPaperAdded)
  useEffect(() => {
    const h = () => { setPaperActiveState(isPaperActive()); setPaperAdded(isPaperAdded()) }
    window.addEventListener(PAPER_EVENT, h)
    return () => window.removeEventListener(PAPER_EVENT, h)
  }, [])

  // Check if there's a stored wallet on mount
  useEffect(() => {
    setOtherWallets(readSlots().map(x => x.pub))
    setActivePub(localStorage.getItem(ACTIVE_PUB_KEY))
    setHasStoredWallet(!!localStorage.getItem(STORAGE_KEY))
    setHasBiometric(!!localStorage.getItem(BIO_KEY))
    // 12h remember: a new tab / page load wipes keypairRef, so silently
    // restore the unlocked key if it was remembered and hasn't expired.
    if (!localStorage.getItem(STORAGE_KEY)) { forget('sol'); return }
    if (isPaperActive()) { forget('sol'); return }   // paper: real key stays locked
    let cancelled = false
    restore('sol').then(secretBase58 => {
      if (cancelled || !secretBase58 || keypairRef.current) return
      try {
        const kp = Keypair.fromSecretKey(bs58decode(secretBase58))
        keypairRef.current = kp
        setPublicKey(kp.publicKey)
        setIsLocked(false)
        markActive(kp.publicKey.toBase58())
      } catch { forget('sol') }
    })
    return () => { cancelled = true }
  }, [])

  // Import a private key (base58 or byte array JSON) and encrypt with password
  const importKey = useCallback(async (privateKeyInput, password) => {
    let secretKey

    // Try parsing as JSON array first (Solana CLI format)
    try {
      const parsed = JSON.parse(privateKeyInput)
      if (Array.isArray(parsed)) {
        secretKey = new Uint8Array(parsed)
      }
    } catch {}

    // Otherwise treat as base58
    if (!secretKey) {
      try {
        secretKey = bs58decode(privateKeyInput.trim())
      } catch (err) {
        throw new Error('Invalid private key format. Use base58 or JSON byte array.')
      }
    }

    // Validate keypair
    const kp = Keypair.fromSecretKey(secretKey)
    const pubkey = kp.publicKey

    // Encrypt the base58 secret key
    const secretBase58 = bs58encode(secretKey)
    const encrypted = await encryptData(secretBase58, password)

    setPaperActive(false)
    localStorage.setItem(STORAGE_KEY, encrypted)
    remember('sol', secretBase58)
    keypairRef.current = kp
    setPublicKey(pubkey)
    setIsLocked(false)
    setHasStoredWallet(true)
    markActive(pubkey.toBase58())

    return pubkey.toBase58()
  }, [])

  // Unlock with password
  const unlock = useCallback(async (password) => {
    const encrypted = localStorage.getItem(STORAGE_KEY)
    if (!encrypted) throw new Error('No wallet found. Import a key first.')

    try {
      const secretBase58 = await decryptData(encrypted, password)
      const secretKey = bs58decode(secretBase58)
      const kp = Keypair.fromSecretKey(secretKey)
      setPaperActive(false)
      remember('sol', secretBase58)

      keypairRef.current = kp
      setPublicKey(kp.publicKey)
      setIsLocked(false)
      markActive(kp.publicKey.toBase58())

      return kp.publicKey.toBase58()
    } catch {
      throw new Error('Wrong password')
    }
  }, [])

  // Lock wallet (clear keypair from memory)
  const lock = useCallback(() => {
    keypairRef.current = null
    forget('sol')
    setIsLocked(true)
    // Keep publicKey visible for display, just can't sign
  }, [])

  // Park the active wallet's blobs in the slots list (returns the new list).
  const parkActive = (slots) => {
    const enc = localStorage.getItem(STORAGE_KEY)
    if (!enc) return slots
    const pub = localStorage.getItem(ACTIVE_PUB_KEY)
      || keypairRef.current?.publicKey?.toBase58()
      || ('wallet-' + Date.now())
    return [...slots.filter(x => x.pub !== pub), { pub, enc, bio: localStorage.getItem(BIO_KEY) || null }]
  }
  // Make a parked blob the active one, LOCKED (its own password unlocks it).
  const activateSlot = (t, rest) => {
    writeSlots(rest)
    localStorage.setItem(STORAGE_KEY, t.enc)
    if (t.bio) localStorage.setItem(BIO_KEY, t.bio); else localStorage.removeItem(BIO_KEY)
    forget('sol')
    keypairRef.current = null
    setPublicKey(null)
    setIsLocked(true)
    setHasStoredWallet(true)
    setHasBiometric(!!t.bio)
    setOtherWallets(rest.map(x => x.pub))
    markActive(t.pub)
  }

  // Add another wallet: park the current one, import the new one as active.
  const addWallet = useCallback(async (privateKeyInput, password) => {
    const kp = parseSecret(privateKeyInput)          // validate before touching storage
    const pub = kp.publicKey.toBase58()
    const cur = localStorage.getItem(ACTIVE_PUB_KEY) || keypairRef.current?.publicKey?.toBase58()
    if (pub === cur) throw new Error('That wallet is already active')
    const slots = parkActive(readSlots()).filter(x => x.pub !== pub)
    writeSlots(slots)
    localStorage.removeItem(BIO_KEY)                 // the print belonged to the parked key
    setHasBiometric(false)
    setOtherWallets(slots.map(x => x.pub))
    return importKey(privateKeyInput, password)
  }, [importKey])

  // Switch to a parked wallet. It comes up locked; unlock with its password.
  const switchWallet = useCallback((pub) => {
    const slots = readSlots()
    const t = slots.find(x => x.pub === pub)
    if (!t) return
    setPaperActive(false)
    activateSlot(t, parkActive(slots.filter(x => x.pub !== pub)))
  }, [])

  // Paper on: drop the real key from memory (same as lock) and flag paper.
  const enterPaper = useCallback(() => {
    keypairRef.current = null
    forget('sol')
    setIsLocked(true)
    setPaperActive(true)
  }, [])
  // Paper off: the real wallet stays locked; its password (or print) unlocks it.
  const exitPaper = useCallback(() => { setPaperActive(false) }, [])

  // Remove wallet entirely
  const removeWallet = useCallback(() => {
    const slots = readSlots()
    if (slots.length) { activateSlot(slots[0], slots.slice(1)); return }   // next saved wallet takes over
    localStorage.removeItem(ACTIVE_PUB_KEY)
    setActivePub(null)
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(BIO_KEY)   // never outlive the key it unlocks
    forget('sol')
    keypairRef.current = null
    setPublicKey(null)
    setIsLocked(true)
    setHasStoredWallet(false)
    setHasBiometric(false)
  }, [])

  // ─── Biometric unlock (WebAuthn PRF) ───
  // Enrollment requires the wallet to be UNLOCKED rather than asking for the
  // password again: the secret is already in memory, and re-prompting adds a
  // typing surface for no security gain. Encrypts the same base58 secret with
  // the PRF-derived key into BIO_KEY.
  const enableBiometric = useCallback(async () => {
    const kp = keypairRef.current
    if (!kp) throw new Error('Unlock the wallet first')
    const secret = await enrollAndGetSecret(PRF_SALT_SOLANA, 'Cheddar Solana wallet')
    const encrypted = await encryptData(bs58encode(kp.secretKey), secret)
    localStorage.setItem(BIO_KEY, encrypted)
    setHasBiometric(true)
  }, [])

  // Same end state as unlock(password) — decrypt, rebuild, expose. A cancelled
  // or unsupported gesture throws and the UI falls back to the password field.
  const unlockWithBiometric = useCallback(async () => {
    const encrypted = localStorage.getItem(BIO_KEY)
    if (!encrypted) throw new Error('Fingerprint unlock is not set up')
    const secret = await getPrfSecret(PRF_SALT_SOLANA)
    let kp
    try {
      kp = Keypair.fromSecretKey(bs58decode(await decryptData(encrypted, secret)))
    } catch {
      // The passkey answered but its secret doesn't open this blob — the
      // passkey was replaced (new device enrollment, keychain reset). Stale
      // envelope: drop it so the UI stops offering an unlock that can't work.
      localStorage.removeItem(BIO_KEY)
      setHasBiometric(false)
      throw new Error('Fingerprint no longer matches this wallet — use your password')
    }
    setPaperActive(false)
    remember('sol', bs58encode(kp.secretKey))
    keypairRef.current = kp
    setPublicKey(kp.publicKey)
    setIsLocked(false)
    markActive(kp.publicKey.toBase58())
    return kp.publicKey.toBase58()
  }, [])

  const disableBiometric = useCallback(() => {
    localStorage.removeItem(BIO_KEY)
    setHasBiometric(false)
  }, [])

  // Sign a transaction instantly — no popups
  const signTransaction = useCallback(async (transaction) => {
    if (isPaperActive()) throw new Error('Paper wallet active — nothing is sent on-chain')
    const kp = keypairRef.current
    if (!kp) throw new Error('Wallet is locked')
    transaction.sign([kp])
    return transaction
  }, [])

  // Memoized: the inline literal handed every consumer a NEW context value per
  // provider render — the same identity-churn bug fixed in TradeDefaults,
  // useTrading, and DualWallet's signTransaction. This provider is the `local`
  // in DualWalletContext, so its churn was the ultimate ROOT of the whole
  // chain that re-rendered the memoized chart ~32x/s. All five functions are
  // useCallback'd; the three state values are the only moving deps.
  const value = useMemo(() => ({
    publicKey,
    signTransaction,
    isLocked,
    hasStoredWallet,
    importKey,
    unlock,
    lock,
    removeWallet,
    hasBiometric,
    enableBiometric,
    unlockWithBiometric,
    disableBiometric,
    otherWallets,
    activePub,
    addWallet,
    switchWallet,
    paperActive,
    paperAdded,
    enterPaper,
    exitPaper,
  }), [publicKey, signTransaction, isLocked, hasStoredWallet, importKey, unlock, lock, removeWallet,
       hasBiometric, enableBiometric, unlockWithBiometric, disableBiometric,
       otherWallets, activePub, addWallet, switchWallet, paperActive, paperAdded, enterPaper, exitPaper])

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  )
}

export function useLocalWallet() {
  const ctx = useContext(WalletContext)
  if (!ctx) throw new Error('useLocalWallet must be used within LocalWalletProvider')
  return ctx
}